/**
 * Painel de configuração do mapeamento coluna→campo do XLS de lote (formato
 * PCP do otimizador). As 26 colunas e seus cabeçalhos são FIXOS (contrato do
 * otimizador); aqui o gestor escolhe QUAL campo do formulário preenche cada
 * uma. Inicia sempre do mapeamento atual da unidade (default = histórico) e,
 * ao salvar, sobrescreve a config no backend.
 */
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { FiRotateCcw } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  obterConfigExportacao,
  salvarConfigExportacao,
} from "../../../services/solicitacoes";

const Intro = styled.p`
  margin: 0 0 14px;
  font-size: 13.5px;
  color: var(--ink-2);
  line-height: 1.5;
`;

const MapaGrid = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
`;

const LinhaMapa = styled.div`
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr) minmax(0, 1.3fr);
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--border);
  &:last-child { border-bottom: none; }

  &.cabecalho {
    background: var(--surface-2, var(--bg));
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--ink-2);
    position: sticky;
    top: 0;
    z-index: 1;
  }
`;

const ColBadge = styled.span`
  display: inline-grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: var(--accent-soft);
  color: var(--accent);
  font-weight: 700;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
`;

const HeaderCol = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CampoSelect = styled.select`
  height: 38px;
  width: 100%;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--ink);
  font-size: 13.5px;
  cursor: pointer;
  &:focus { outline: none; border-color: var(--accent); }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const RestaurarBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink-2);
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  &:hover { border-color: var(--accent); color: var(--accent); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const paraMapa = (arr) =>
  Object.fromEntries((arr || []).map((e) => [e.coluna, e.campo_key]));

export default function MapeamentoExportacaoPanel({ open, onClose, onSaved }) {
  const { request } = useAuth();
  const [colunas, setColunas] = useState([]); // [{coluna, header}]
  const [campos, setCampos] = useState([]); // [{campo_key, label}]
  const [mapa, setMapa] = useState({}); // {coluna: campo_key}
  const [padrao, setPadrao] = useState({}); // {coluna: campo_key}
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let vivo = true;
    setLoading(true);
    setErro(null);
    obterConfigExportacao(request)
      .then((d) => {
        if (!vivo) return;
        setColunas(d.colunas || []);
        setCampos(d.campos_disponiveis || []);
        setMapa(paraMapa(d.mapeamento));
        setPadrao(paraMapa(d.padrao));
      })
      .catch((e) => vivo && setErro(e?.message || "Erro ao carregar a configuração."))
      .finally(() => {
        if (vivo) setLoading(false);
      });
    return () => {
      vivo = false;
    };
  }, [open, request]);

  const setCampo = useCallback((coluna, campoKey) => {
    setMapa((prev) => ({ ...prev, [coluna]: campoKey }));
  }, []);

  const restaurarPadrao = useCallback(() => setMapa({ ...padrao }), [padrao]);

  const salvar = useCallback(async () => {
    const mapeamento = colunas.map((c) => ({
      coluna: c.coluna,
      campo_key: mapa[c.coluna] || "vazio",
    }));
    await salvarConfigExportacao(request, mapeamento);
    onSaved?.();
    onClose?.();
  }, [colunas, mapa, request, onSaved, onClose]);

  const alterado =
    colunas.length > 0 &&
    colunas.some((c) => (mapa[c.coluna] || "vazio") !== (padrao[c.coluna] || "vazio"));

  const footer = (
    <Toolbar>
      <RestaurarBtn type="button" onClick={restaurarPadrao} disabled={!alterado}>
        <FiRotateCcw size={14} /> Restaurar padrão
      </RestaurarBtn>
      <AsyncButton
        variant="primary"
        successMessage="Mapeamento salvo."
        onClick={salvar}
        disabled={loading || !colunas.length}
      >
        Salvar mapeamento
      </AsyncButton>
    </Toolbar>
  );

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title="Mapeamento da planilha de exportação"
      subtitle="Escolha qual campo do formulário preenche cada coluna do XLS do otimizador."
      width="clamp(680px, 55vw, 96vw)"
      footer={footer}
    >
      <Intro>
        As 26 colunas e seus cabeçalhos são fixos (contrato do otimizador de corte).
        Você só escolhe o <strong>campo do formulário</strong> que alimenta cada uma.
        A configuração vale para toda a unidade e passa a valer nos próximos lotes gerados.
      </Intro>

      {loading ? (
        <Intro>Carregando configuração…</Intro>
      ) : erro ? (
        <Intro>{erro}</Intro>
      ) : (
        <MapaGrid>
          <LinhaMapa className="cabecalho">
            <span>Col.</span>
            <span>Cabeçalho (fixo)</span>
            <span>Campo do formulário</span>
          </LinhaMapa>
          {colunas.map((c) => (
            <LinhaMapa key={c.coluna}>
              <ColBadge>{c.coluna}</ColBadge>
              <HeaderCol title={c.header}>{c.header}</HeaderCol>
              <CampoSelect
                value={mapa[c.coluna] || "vazio"}
                onChange={(e) => setCampo(c.coluna, e.target.value)}
                aria-label={`Campo da coluna ${c.coluna} (${c.header})`}
              >
                {campos.map((f) => (
                  <option key={f.campo_key} value={f.campo_key}>
                    {f.label}
                  </option>
                ))}
              </CampoSelect>
            </LinhaMapa>
          ))}
        </MapaGrid>
      )}
    </SlideOverPanel>
  );
}
